import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, getAccessKey } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AuthModal } from "@/components/app/AuthModal";

const AccessContext = createContext(null);
export const DEFAULT_PLAN = "oracle_sub_monthly";

export function AccessProvider({ children }) {
  const { user } = useAuth();
  const [access, setAccess] = useState({ active: false, kind: null, expires_at: null, price_usd: 4.99, trial_days: 3, plans: [] });
  const [loading, setLoading] = useState(true);
  const [checkingOut, setCheckingOut] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [pendingPlan, setPendingPlan] = useState(null);

  const refresh = useCallback(async (sync = false) => {
    try {
      const { data } = await api.get(sync ? "/access?sync=1" : "/access");
      setAccess(data);
      return data;
    } catch {
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh, user]);

  useEffect(() => {
    const ref = new URLSearchParams(window.location.search).get("ref");
    if (ref) {
      localStorage.setItem("pb_ref", ref.toUpperCase());
      api.post("/referral/click", { code: ref, access_key: getAccessKey() }).catch(() => {});
    }
  }, []);

  useEffect(() => {
    if (sessionStorage.getItem("pb_visit_tracked")) return;
    sessionStorage.setItem("pb_visit_tracked", "1");
    api.post("/track/visit", { access_key: getAccessKey(), path: window.location.pathname }).catch(() => {});
  }, []);

  const startCheckout = useCallback(async (lookupKey = DEFAULT_PLAN, provider = "stripe") => {
    setCheckingOut(true);
    try {
      const body = { lookup_key: lookupKey, origin_url: window.location.origin, access_key: getAccessKey(), referral_code: localStorage.getItem("pb_ref") || null };
      const { data } = provider === "paypal" ? await api.post("/paypal/subscribe", body) : await api.post("/payments/checkout", body);
      window.location.href = data.checkout_url || data.approve_url;
    } catch (e) {
      setCheckingOut(false);
      throw e;
    }
  }, []);

  // Subscriptions need an account: open sign-in first, then continue to checkout automatically.
  const subscribe = useCallback(async (lookupKey = DEFAULT_PLAN, provider = "stripe") => {
    if (!user) {
      setPendingPlan({ lookupKey, provider });
      setAuthOpen(true);
      return;
    }
    await startCheckout(lookupKey, provider);
  }, [user, startCheckout]);

  useEffect(() => {
    if (user && pendingPlan) {
      const { lookupKey, provider } = pendingPlan;
      setPendingPlan(null);
      setAuthOpen(false);
      startCheckout(lookupKey, provider).catch(() => {});
    }
  }, [user, pendingPlan, startCheckout]);

  useEffect(() => {
    if (!authOpen && !user && pendingPlan) setPendingPlan(null); // dismissed without signing in
  }, [authOpen, user, pendingPlan]);

  // One-time 30-day pass via PayPal — works for guests (tied to this browser) and signed-in users.
  const buyPassWithPaypal = useCallback(async () => {
    setCheckingOut(true);
    try {
      const { data } = await api.post("/paypal/pass", { origin_url: window.location.origin, access_key: getAccessKey(), referral_code: localStorage.getItem("pb_ref") || null });
      window.location.href = data.approve_url;
    } catch (e) {
      setCheckingOut(false);
      throw e;
    }
  }, []);

  const cancelPaypal = useCallback(async () => {
    const { data } = await api.post("/paypal/subscription/cancel", {});
    await refresh(true);
    return data;
  }, [refresh]);

  const openPortal = useCallback(async () => {
    const { data } = await api.post("/payments/portal", { origin_url: window.location.origin });
    window.location.href = data.url;
  }, []);

  const daysLeft = access.expires_at
    ? Math.max(0, Math.ceil((new Date(access.expires_at) - Date.now()) / 86400000))
    : 0;

  return (
    <AccessContext.Provider value={{ ...access, loading, checkingOut, daysLeft, refresh, startCheckout, subscribe, buyPassWithPaypal, cancelPaypal, openPortal, openAuth: () => setAuthOpen(true) }}>
      {children}
      <AuthModal open={authOpen} onOpenChange={setAuthOpen} subscribing={!!pendingPlan} />
    </AccessContext.Provider>
  );
}

export const useAccess = () => useContext(AccessContext);
